import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import { Helmet } from 'react-helmet';
import { TextV2, TabView } from '@insights-ltd/design-library/components';
import FullHeight from 'components/FullHeight';
import Breadcrumbs from 'components/Breadcrumbs';
import useHistoryState, {
  LocationStateProvider,
} from 'components/hooks/useHistoryState';
import useTimeout from 'components/hooks/useTimeout';
import useGetMyOrganisations from 'components/hooks/useGetMyOrganisations';
import { FullScreenError, FullScreenSpinner } from 'components/FullScreen';
import Grid from '@mui/material/Grid';
import { ChangeOrganisation } from 'components/ChangeOrganisation';
import { queryStatusIgnoreIdle } from 'utils/combineQueryStatus';
import { spacingSizeMap } from '@insights-ltd/design-library/themes';
import { useSearchContext } from 'components/SearchFilter/SearchProvider';
import { useLocation, useNavigate } from 'react-router';
import LearnersTab from './LearnersTab';
import TeamsTab from './TeamsTab';

type LearnerView = 'learners' | 'teams';

const NOTIFICATION_TIMEOUT = 20_000;

const Learners = () => {
  const { t } = useTranslation();

  const [isOrgPickerOpen, setIsOrgPickerOpen] = useState(false);
  const {
    searchState: { resource: tabValue, organisation },
    setSearchState,
  } = useSearchContext();

  const { state: locationState, pathname } = useLocation();
  const navigatedToOrganisation = locationState?.organisation;
  const navigate = useNavigate();
  useEffect(() => {
    if (navigatedToOrganisation) {
      setSearchState({
        organisation: navigatedToOrganisation,
      });
      navigate(pathname, { replace: true, state: {} });
    }
  }, [navigate, navigatedToOrganisation, pathname, setSearchState]);

  const [deletedLearner, setDeletedLearner] = useHistoryState<
    string | undefined
  >('deletedLearner');

  const [, setDeletedTeam] = useHistoryState<string | undefined>('deletedTeam');

  const notification = deletedLearner
    ? t('ui.event-management.learners.teams.delete-success', {
        teamName: deletedLearner,
      })
    : undefined;

  const handleSearchState = (state: LearnerView) => {
    if (state !== tabValue) {
      setSearchState({ resource: state, searchTerm: '', checkbox: undefined });
      setDeletedTeam(undefined);
      setDeletedLearner(undefined);
    }
  };

  useTimeout(() => setDeletedLearner(undefined), NOTIFICATION_TIMEOUT);
  useTimeout(() => setDeletedTeam(undefined), NOTIFICATION_TIMEOUT);

  const myOrganisationsQuery = useGetMyOrganisations();
  const responseStatus = queryStatusIgnoreIdle(myOrganisationsQuery);
  const [firstOrganisation] = myOrganisationsQuery.organisations ?? [];
  const selectedOrganisationId =
    !myOrganisationsQuery.hasMultipleOrgs && firstOrganisation
      ? firstOrganisation.id
      : (organisation?.id ?? null);

  if (responseStatus === 'pending') {
    return <FullScreenSpinner message={t('ui.event-management.loading')} />;
  }
  if (responseStatus === 'error') {
    return (
      <FullScreenError
        message={t('ui.event-management.learners.error-loading-learners')}
      />
    );
  }

  return (
    <>
      <Helmet>
        <title>{t('ui.event-management.title.my-learners')}</title>
      </Helmet>
      <FullHeight>
        <Container maxWidth="lg">
          <Box
            sx={{
              py: (theme) => theme.spacing(spacingSizeMap.M),
            }}
          >
            <Breadcrumbs
              crumbs={{
                '/': t('ui.event-management.dashboard.home.title'),
              }}
              activeText={t('ui.event-management.learners-and-teams.title')}
            />
          </Box>
          <Grid
            container
            direction="row"
            sx={{
              justifyContent: 'space-between',
            }}
          >
            <Grid size={{ xs: 12, md: 6 }}>
              <Box
                sx={{
                  mb: (theme) => theme.spacing(spacingSizeMap.S),
                }}
              >
                <TextV2 component="h1" variant="h2">
                  {t('ui.event-management.learners-and-teams.title')}
                </TextV2>
              </Box>
              <Box
                sx={{
                  mb: (theme) => theme.spacing(spacingSizeMap.S),
                }}
              >
                <TextV2 color="textSecondary">
                  {t('ui.event-management.learners-and-teams.sub-title')}
                </TextV2>
              </Box>
            </Grid>
            <Grid size={{ xs: 12, md: 'auto' }}>
              {myOrganisationsQuery.hasMultipleOrgs && (
                <ChangeOrganisation
                  disabled={false}
                  organisation={organisation ?? null}
                  handleOrganisationChange={(value) => {
                    setSearchState({ organisation: value });
                    setIsOrgPickerOpen(false);
                  }}
                  handleOnClose={() => {
                    setIsOrgPickerOpen(false);
                  }}
                  open={isOrgPickerOpen}
                  hasAllOrgsOption={myOrganisationsQuery.hasMultipleOrgs}
                />
              )}
            </Grid>
          </Grid>
          <Box
            sx={{
              mb: (theme) => theme.spacing(spacingSizeMap.M),
            }}
          >
            <TabView
              onChange={(_, value: LearnerView) => handleSearchState(value)}
              value={(tabValue as LearnerView) ?? 'learners'}
              options={[
                {
                  header: t('ui.event-management.learners.learners-tab'),
                  value: 'learners',
                  content: (
                    <LearnersTab
                      organisationId={selectedOrganisationId}
                      handleOpenOrgSelector={() => {
                        setIsOrgPickerOpen(true);
                      }}
                      notification={notification}
                    />
                  ),
                },
                {
                  header: t('ui.event-management.learners.all-teams'),
                  value: 'teams',
                  content: (
                    <TeamsTab
                      organisationId={selectedOrganisationId}
                      key={selectedOrganisationId}
                      handleOpenOrgSelector={() => {
                        setIsOrgPickerOpen(true);
                      }}
                    />
                  ),
                },
              ]}
            />
          </Box>
        </Container>
      </FullHeight>
    </>
  );
};

const WrappedLearners = () => (
  <LocationStateProvider>
    <Learners />
  </LocationStateProvider>
);

export default WrappedLearners;
